import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Save } from "lucide-react";
import Form, { Field } from "../../../../../components/shared/Form";
import Select from "../../../../../components/shared/inputs/Select";
import MoneyInput, { type MoneyInputProps } from "../../../../../components/shared/inputs/MoneyInput";
import Button from "../../../../../components/shared/Button";
import { useCompanyCurrency } from "../../../../../utils/hooks/company";
import { useIsMasterCompany } from "../../../../../utils/hooks/user";
import { renderSettingsResourceLabel } from "../../../../../utils/helpers/settings";
import { createRateSchema, type RateFormValues } from "./zodValidation";

function MoneyField({ onChange, value, ...props }: MoneyInputProps & { onChange?: (value?: string) => void }) {
    return (
        <MoneyInput
            value={value as any}
            onValueChange={onChange}
            {...props}
        />
    );
}

function RateForm({ mode, values, config, mutate, isPending, roles, close }: any) {
    const { t } = useTranslation();
    const currency = useCompanyCurrency();
    const isMasterCompany = useIsMasterCompany();

    const schema = useMemo(() => createRateSchema(t, roles), [t, roles]);

    const form = useForm<RateFormValues>({
        resolver: zodResolver(schema),
        defaultValues: {
            role: values?.role,
            maxRate: values?.maxRate ?? undefined,
            maxAmount: values?.maxAmount ?? undefined,
        },
    });

    const { control, reset } = form;

    const onClose = useCallback(() => {
        reset();
        close();
    }, [close, reset]);

    const onFinish = (value: RateFormValues) => {
        const entry = {
            role: value.role,
            maxRate: value.maxRate ? Number(value.maxRate) : undefined,
            maxAmount: Number(value.maxAmount),
        };
        const updated = mode === "create"
            ? [...config, entry]
            : [...(config?.filter((item: any) => item.role !== value.role) ?? []), entry];
        mutate(updated, {
            onSuccess: () => onClose()
        });
    };

    const selectedRole = useWatch({ control, name: "role" });

    const canUpdateRate = useMemo(() => {
        const role = roles?.find((r: any) => r.id === selectedRole);
        return role?.permissions?.timeSheet?.canUpdateRate;
    }, [selectedRole, roles]);

    const availableRoles = useMemo(() => {
        const configuredIds = config?.map(({ role }: any) => role) ?? [];
        return roles?.filter((role: any) =>
            !configuredIds.includes(role.id) || role.id === values?.role
        ) ?? [];
    }, [roles, config, values?.role]);

    return (
        <Form className="tw-flex-1 tw-basis-72" form={form} onFinish={onFinish}>
            <Field name="role" label={t("role")} required className="tw-mb-4">
                <Select disabled={mode === "edit"} placeholder={t("role")}>
                    {availableRoles.map((role: any) => (
                        <Select.Item key={role.id} value={role.id}>
                            {renderSettingsResourceLabel(role)}
                        </Select.Item>
                    ))}
                </Select>
            </Field>

            {canUpdateRate && (
                <Field name="maxRate" label={t("max-rate")} required className="tw-mb-4" shouldUnregister={false}>
                    <MoneyField decimalsLimit={2} currency={currency} placeholder={t("max-rate")} />
                </Field>
            )}

            <Field name="maxAmount" label={t("max-amount")} required className="tw-mb-4">
                <MoneyField decimalsLimit={2} currency={currency} placeholder={t("max-amount")} />
            </Field>

            <div className="tw-flex tw-justify-end tw-gap-x-2">
                <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={onClose}
                    disabled={isPending}
                >
                    {t("cancel")}
                </Button>

                {isMasterCompany && (
                    <Button
                        size="sm"
                        icon={<Save />}
                        loading={isPending}
                        disabled={isPending}
                    >
                        {t("save")}
                    </Button>
                )}
            </div>
        </Form>
    );
}

export default RateForm;
